// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { CPT, createReference, formatHumanName, getReferenceString, type MedplumClient } from '@medplum/core';
import type {
  Appointment,
  ChargeItem,
  Encounter,
  Patient,
  Practitioner,
  Resource,
  Schedule,
  Slot,
} from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition, BookAppointmentResult } from '@polarissleep/scheduling';
import {
  ensurePractitionerOnCareTeam,
  REPLACES_APPOINTMENT_EXTENSION_URL,
  resolveChargeForAppointmentType,
  bookAppointment as sharedBookAppointment,
} from '@polarissleep/scheduling';
import type { ProposedAppointment, SchedulingTarget } from '@polarissleep/scheduling-next';
import { SCHEDULING_V2_PROVENANCE_TAG, withTenantAndEnvironmentScope } from '@polarissleep/scheduling-next';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  isBundleCoveredAppointment,
  isSelfPayPatient,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { getConfig } from '../config';
import { botIdentifier } from '../lib/bot-identifier';
import { getPatientSchedulingScope } from '../scheduling-next/availability';
import { bookAppointmentThroughBot } from '../scheduling-next/patient-appointment-bot';
import type { PatientAppointmentCandidate } from '../scheduling-next/types';
import { isPatientBookableStart } from '../utils/patient-scheduling-notice';
import { getDeviceTimezone } from '../utils/patient-timezone';
import { withTenantScope } from '../utils/tenant';

interface BookAppointmentParams {
  patient: Patient;
  slotId: string;
  appointmentTypeCode: string;
  providerId: string;
  start: string;
  end: string;
  appointmentType?: AppointmentTypeDefinition;
}

export async function bookAppointment(
  medplum: MedplumClient,
  params: BookAppointmentParams
): Promise<BookAppointmentResult> {
  throw new Error('Legacy appointment booking is disabled. Select a V2 availability slot and try again.');
  assertPatientBookingNotice(params.start);
  const result = await sharedBookAppointment(medplum, {
    ...params,
    insuranceDeferredBillingEnabled: getConfig().insuranceDeferredBillingEnabled,
    decorate: withTenantScope as <T extends { resourceType: string }>(r: T) => T,
  });

  await medplum.executeBot(
    botIdentifier('create-zoom-meetings'),
    { appointmentId: result.appointmentId },
    'application/json'
  );

  return result;
}

/** Books a proposed Medplum Scheduling API appointment and finalizes Polaris resources. */
export async function bookV2Appointment(
  medplum: MedplumClient,
  params: {
    patient: Patient;
    candidate: PatientAppointmentCandidate;
    appointmentType: AppointmentTypeDefinition;
    replacesAppointmentId?: string;
  }
): Promise<BookAppointmentResult> {
  assertPatientBookingNotice(params.candidate.appointment.start);
  const scope = getPatientSchedulingScope();
  const target = await resolveSchedulingTarget(medplum, params.candidate, scope);
  const proposedAppointment = addBookingParticipants(
    params.candidate.appointment,
    params.patient,
    params.appointmentType,
    params.replacesAppointmentId
  );
  const appointment = await bookAppointmentThroughBot(medplum, proposedAppointment, getDeviceTimezone() || undefined);
  let encounterId = '';
  let requiresPayment = false;

  try {
    const encounter = await medplum.createResource(
      withTenantAndEnvironmentScope(
        {
          resourceType: 'Encounter',
          status: 'planned',
          class: {
            system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
            code: params.appointmentType.mode === 'virtual' ? 'VR' : 'AMB',
            display: params.appointmentType.mode === 'virtual' ? 'virtual' : 'ambulatory',
          },
          type: [{ coding: [params.appointmentType.coding], text: params.appointmentType.display }],
          subject: createReference(params.patient),
          appointment: [createReference(appointment)],
          participant: [{ individual: { reference: `Practitioner/${target.actor.id}` } }],
        } satisfies Encounter,
        scope.organizationId,
        scope.environment
      )
    );
    encounterId = encounter.id ?? '';

    const suppressForBundle = await isBundleCoveredAppointment(
      medplum,
      params.patient,
      params.appointmentType.coding.code ?? '',
      { appointmentStart: appointment.start }
    );
    const deferInsuranceBilling = getConfig().insuranceDeferredBillingEnabled && !isSelfPayPatient(params.patient);
    const chargeSpec =
      suppressForBundle || deferInsuranceBilling
        ? null
        : await resolveChargeForAppointmentType(
            medplum,
            params.appointmentType,
            params.patient,
            appointment.start ?? new Date().toISOString()
          );
    requiresPayment = Boolean(chargeSpec);

    if (chargeSpec && encounter.id && appointment.id) {
      await medplum.createResource(
        withTenantAndEnvironmentScope(
          {
            resourceType: 'ChargeItem',
            status: 'planned',
            subject: createReference(params.patient),
            context: createReference(encounter),
            service: [createReference(appointment)] as unknown as ChargeItem['service'],
            code: { coding: [{ system: CPT, code: chargeSpec.cptCode, display: params.appointmentType.display }] },
            definitionCanonical: [chargeSpec.chargeItemDefinitionUrl],
            occurrenceDateTime: appointment.start,
            identifier: [{ system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: appointment.id }],
            priceOverride: { value: chargeSpec.amountUsd, currency: 'USD' },
            quantity: { value: 1 },
          } satisfies ChargeItem,
          scope.organizationId,
          scope.environment
        )
      );
    }

    if (target.actor.resourceType !== 'Practitioner') {
      throw new Error('Patient v2 booking requires a Practitioner schedule actor.');
    }
    await ensurePractitionerOnCareTeam(medplum, params.patient, target.actor, {
      decorate: <T extends { resourceType: string }>(resource: T): T =>
        withTenantAndEnvironmentScope(resource as Resource, scope.organizationId, scope.environment) as T,
    });
  } catch (error) {
    // `$book` already committed the Appointment. Keep it recoverable and report finalization failure.
    Sentry.captureException(error, {
      extra: { appointmentId: appointment.id, operation: 'patient-v2-book-finalization' },
    });
  }

  try {
    await medplum.executeBot(
      botIdentifier('create-zoom-meetings'),
      { appointmentId: appointment.id },
      'application/json'
    );
  } catch (error) {
    Sentry.captureException(error, {
      extra: { appointmentId: appointment.id, operation: 'patient-v2-zoom-finalization' },
    });
  }

  return {
    appointmentId: appointment.id as string,
    encounterId,
    requiresPayment,
  };
}

function assertPatientBookingNotice(start: string | undefined): void {
  if (!isPatientBookableStart(start)) {
    throw new Error('Patients cannot book tomorrow. Please choose a date at least two calendar days from today.');
  }
}

async function resolveSchedulingTarget(
  medplum: MedplumClient,
  candidate: PatientAppointmentCandidate,
  scope: ReturnType<typeof getPatientSchedulingScope>
): Promise<SchedulingTarget> {
  const containedSlot = candidate.appointment.contained?.find(
    (resource): resource is Slot => resource.resourceType === 'Slot'
  );
  const scheduleReference = containedSlot?.schedule?.reference;
  const scheduleId = scheduleReference?.startsWith('Schedule/')
    ? scheduleReference.slice('Schedule/'.length)
    : undefined;
  if (!scheduleId || !candidate.serviceId || !candidate.providerId) {
    throw new Error('Selected appointment is missing scheduling resources. Please refresh and choose another time.');
  }

  const [healthcareService, schedule, actor] = await Promise.all([
    medplum.readResource('HealthcareService', candidate.serviceId),
    medplum.readResource('Schedule', scheduleId) as Promise<Schedule>,
    medplum.readResource('Practitioner', candidate.providerId) as Promise<Practitioner>,
  ]);

  return { healthcareService, schedule, actor, scope };
}

function addBookingParticipants(
  appointment: ProposedAppointment,
  patient: Patient,
  appointmentType: AppointmentTypeDefinition,
  replacesAppointmentId?: string
): ProposedAppointment {
  const participants = appointment.participant ?? [];
  const patientReference = getReferenceString(patient);
  return {
    ...appointment,
    meta: {
      ...appointment.meta,
      tag: [
        ...(appointment.meta?.tag ?? []).filter((tag) => tag.system !== SCHEDULING_V2_PROVENANCE_TAG.system),
        SCHEDULING_V2_PROVENANCE_TAG,
      ],
    },
    appointmentType: { coding: [appointmentType.coding], text: appointmentType.display },
    ...(replacesAppointmentId
      ? {
          extension: [
            ...(appointment.extension ?? []).filter(
              (extension) => extension.url !== REPLACES_APPOINTMENT_EXTENSION_URL
            ),
            {
              url: REPLACES_APPOINTMENT_EXTENSION_URL,
              valueReference: { reference: `Appointment/${replacesAppointmentId}` },
            },
          ],
        }
      : {}),
    participant: [
      ...participants,
      ...(participants.some((participant) => participant.actor?.reference === patientReference)
        ? []
        : [
            {
              actor: { reference: patientReference, display: formatHumanName(patient.name?.[0] ?? {}) },
              status: 'accepted' as const,
            },
          ]),
    ],
  };
}
